/**
 * Workload Tracking Analytics Service - Phase 4 Architecture
 *
 * Encapsulates workload analytics, summaries, trends, and anomaly detection.
 * Extracted from WorkloadTrackingService to reduce single-responsibility violations.
 */

import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type { WorkloadSummary, WorkloadTracking, WorkloadTrend } from "@/types/time-management";
import { WorkloadTrackingCrudService } from "./workload-tracking-crud.service";

export class WorkloadTrackingAnalyticsService implements IService {
  public readonly name = "WorkloadTrackingAnalyticsService";
  private crudService: WorkloadTrackingCrudService;

  constructor(crudService: WorkloadTrackingCrudService) {
    this.crudService = crudService;
  }

  async initialize(): Promise<void> {
    logger.info("✅ WorkloadTrackingAnalyticsService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ WorkloadTrackingAnalyticsService disposed");
  }

  async getWorkloadSummary(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<WorkloadSummary>> {
    try {
      const workloadResult = await this.crudService.getUserWorkload(userId, dateRange);
      if (workloadResult.error || !workloadResult.data) {
        return createErrorResponse(
          workloadResult.error ?? "Failed to load workload"
        );
      }

      const workloadData = workloadResult.data;

      const totalPlannedHours = workloadData.reduce(
        (sum, entry) => sum + entry.planned_hours,
        0
      );
      const totalActualHours = workloadData.reduce(
        (sum, entry) => sum + entry.actual_hours,
        0
      );
      const totalTasks = workloadData.reduce(
        (sum, entry) => sum + entry.task_count,
        0
      );
      const totalCompleted = workloadData.reduce(
        (sum, entry) => sum + entry.completed_tasks,
        0
      );

      const completionRate =
        totalTasks > 0 ? (totalCompleted / totalTasks) * 100 : 0;

      const productivityScores = workloadData
        .filter((entry) => entry.productivity_score !== null)
        .map((entry) => entry.productivity_score!);

      const averageProductivityScore =
        productivityScores.length > 0
          ? productivityScores.reduce((sum, score) => sum + score, 0) /
            productivityScores.length
          : 0;

      const trends = this.calculateTrends(workloadData);

      const summary: WorkloadSummary = {
        total_planned_hours: totalPlannedHours,
        total_actual_hours: totalActualHours,
        total_tasks: totalTasks,
        total_completed: totalCompleted,
        completion_rate: completionRate,
        average_productivity_score: averageProductivityScore,
        trends,
      };

      return createSuccessResponse(summary);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async getProductivityTrends(
    userId: string,
    dateRange: { start: string; end: string },
    metric: "hours" | "tasks" | "productivity"
  ): Promise<ServiceResponse<WorkloadTrend[]>> {
    try {
      const workloadResult = await this.crudService.getUserWorkload(userId, dateRange);
      if (workloadResult.error || !workloadResult.data) {
        return createErrorResponse(
          workloadResult.error ?? "Failed to load workload"
        );
      }

      const workloadData = workloadResult.data;
      const trends: WorkloadTrend[] = [];

      for (let i = 0; i < workloadData.length; i++) {
        const entry = workloadData[i]!;
        const value = this.getMetricValue(entry, metric);
        const prevEntry = i > 0 ? workloadData[i - 1]! : null;
        const changePercentage =
          prevEntry !== null
            ? (() => {
                const prevValue = this.getMetricValue(prevEntry, metric);
                return prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
              })()
            : 0;

        trends.push({
          date: entry.date,
          metric,
          value,
          change_percentage: changePercentage,
        });
      }

      return createSuccessResponse(trends);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async calculateCapacityUtilization(
    userId: string,
    dateRange: { start: string; end: string },
    targetHours = 8
  ): Promise<
    ServiceResponse<{
      averageUtilization: number;
      peakUtilization: number;
      underutilizedDays: number;
      overutilizedDays: number;
      recommendations: string[];
    }>
  > {
    try {
      const workloadResult = await this.crudService.getUserWorkload(userId, dateRange);
      if (workloadResult.error || !workloadResult.data) {
        return createErrorResponse(
          workloadResult.error ?? "Failed to load workload"
        );
      }

      const workloadData = workloadResult.data;
      const utilizationRates = workloadData.map(
        (entry) => entry.actual_hours / targetHours
      );

      const averageUtilization =
        utilizationRates.reduce((sum, rate) => sum + rate, 0) /
          utilizationRates.length || 0;
      const peakUtilization = Math.max(...utilizationRates, 0);

      const underutilizedDays = utilizationRates.filter(
        (rate) => rate < 0.6
      ).length;
      const overutilizedDays = utilizationRates.filter(
        (rate) => rate > 0.9
      ).length;

      const recommendations: string[] = [];

      if (averageUtilization < 0.6) {
        recommendations.push("Consider increasing workload or adjusting targets");
      } else if (averageUtilization > 0.9) {
        recommendations.push("High utilization - monitor for burnout");
      }

      if (underutilizedDays > 10) {
        recommendations.push("Significant underutilization detected");
      }

      if (overutilizedDays > 5) {
        recommendations.push("Frequent overutilization - adjust workload distribution");
      }

      return createSuccessResponse({
        averageUtilization,
        peakUtilization,
        underutilizedDays,
        overutilizedDays,
        recommendations,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async identifyWorkloadPatterns(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      weeklyPatterns: Record<string, number>;
      monthlyPatterns: Record<string, number>;
      seasonalTrends: WorkloadTrend[];
      anomalies: {
        date: string;
        metric: string;
        expected: number;
        actual: number;
        deviation: number;
      }[];
    }>
  > {
    try {
      const workloadResult = await this.crudService.getUserWorkload(userId, dateRange);
      if (workloadResult.error || !workloadResult.data) {
        return createErrorResponse(
          workloadResult.error ?? "Failed to load workload"
        );
      }

      const workloadData = workloadResult.data;

      const weeklyPatterns: Record<string, number> = {};
      const weeklyData: Record<number, number[]> = {};

      workloadData.forEach((entry) => {
        const dayOfWeek = new Date(entry.date).getDay();
        (weeklyData[dayOfWeek] ??= []).push(entry.actual_hours);
      });

      Object.keys(weeklyData).forEach((day) => {
        const values = weeklyData[parseInt(day)] ?? [];
        const average =
          values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0;
        weeklyPatterns[day] = Math.round(average * 10) / 10;
      });

      const monthlyPatterns: Record<string, number> = {};
      const monthlyData: Record<number, number[]> = {};

      workloadData.forEach((entry) => {
        const month = new Date(entry.date).getMonth();
        (monthlyData[month] ??= []).push(entry.actual_hours);
      });

      Object.keys(monthlyData).forEach((month) => {
        const values = monthlyData[parseInt(month)] ?? [];
        const average =
          values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0;
        monthlyPatterns[month] = Math.round(average * 10) / 10;
      });

      const seasonalTrends = await this.getProductivityTrends(userId, dateRange, "hours");
      const trends = seasonalTrends.data ?? [];

      const anomalies: {
        date: string;
        metric: string;
        expected: number;
        actual: number;
        deviation: number;
      }[] = [];

      const avgHours =
        workloadData.reduce((sum, entry) => sum + entry.actual_hours, 0) /
        workloadData.length;
      const stdDev = Math.sqrt(
        workloadData.reduce(
          (sum, entry) => sum + Math.pow(entry.actual_hours - avgHours, 2),
          0
        ) / workloadData.length
      );

      workloadData.forEach((entry) => {
        const deviation = Math.abs(entry.actual_hours - avgHours);
        if (deviation > stdDev * 2) {
          anomalies.push({
            date: entry.date,
            metric: "actual_hours",
            expected: avgHours,
            actual: entry.actual_hours,
            deviation,
          });
        }
      });

      return createSuccessResponse({
        weeklyPatterns,
        monthlyPatterns,
        seasonalTrends: trends,
        anomalies,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async detectDataAnomalies(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      anomalies: {
        date: string;
        field: string;
        value: number;
        expectedRange: [number, number];
        severity: "low" | "medium" | "high";
      }[];
      qualityScore: number;
    }>
  > {
    try {
      const workloadResult = await this.crudService.getUserWorkload(userId, dateRange);
      if (workloadResult.error || !workloadResult.data) {
        return createErrorResponse(
          workloadResult.error ?? "Failed to load workload"
        );
      }

      const anomalies: {
        date: string;
        field: string;
        value: number;
        expectedRange: [number, number];
        severity: "low" | "medium" | "high";
      }[] = [];

      const workloadData = workloadResult.data;
      const avgHours =
        workloadData.reduce((sum, entry) => sum + entry.actual_hours, 0) /
        workloadData.length;
      const stdDev = Math.sqrt(
        workloadData.reduce(
          (sum, entry) => sum + Math.pow(entry.actual_hours - avgHours, 2),
          0
        ) / workloadData.length
      );

      workloadData.forEach((entry) => {
        const deviation = Math.abs(entry.actual_hours - avgHours);
        if (deviation > stdDev * 2) {
          anomalies.push({
            date: entry.date,
            field: "actual_hours",
            value: entry.actual_hours,
            expectedRange: [avgHours - stdDev, avgHours + stdDev],
            severity: deviation > stdDev * 3 ? "high" : "medium",
          });
        }
      });

      const qualityScore = Math.max(0, 1 - anomalies.length / workloadData.length);

      return createSuccessResponse({ anomalies, qualityScore });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  private getMetricValue(
    entry: WorkloadTracking,
    metric: "hours" | "tasks" | "productivity"
  ): number {
    switch (metric) {
      case "hours":
        return entry.actual_hours;
      case "tasks":
        return entry.completed_tasks;
      case "productivity":
        return entry.productivity_score ?? 0;
      default:
        return 0;
    }
  }

  private generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]!);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  private calculateTrends(workloadData: WorkloadTracking[]): WorkloadTrend[] {
    const trends: WorkloadTrend[] = [];

    const sortedData = [...workloadData].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    sortedData.forEach((entry, index) => {
      if (index > 0) {
        const prevEntry = sortedData[index - 1]!;

        const hoursChange =
          prevEntry.actual_hours > 0
            ? ((entry.actual_hours - prevEntry.actual_hours) /
                prevEntry.actual_hours) *
              100
            : 0;

        trends.push({
          date: entry.date,
          metric: "hours",
          value: entry.actual_hours,
          change_percentage: hoursChange,
        });

        const tasksChange =
          prevEntry.completed_tasks > 0
            ? ((entry.completed_tasks - prevEntry.completed_tasks) /
                prevEntry.completed_tasks) *
              100
            : 0;

        trends.push({
          date: entry.date,
          metric: "tasks",
          value: entry.completed_tasks,
          change_percentage: tasksChange,
        });

        if (
          entry.productivity_score !== null &&
          entry.productivity_score !== undefined &&
          prevEntry.productivity_score !== null &&
          prevEntry.productivity_score !== undefined
        ) {
          const productivityChange =
            prevEntry.productivity_score > 0
              ? ((entry.productivity_score - prevEntry.productivity_score) /
                  prevEntry.productivity_score) *
                100
              : 0;

          trends.push({
            date: entry.date,
            metric: "productivity",
            value: entry.productivity_score,
            change_percentage: productivityChange,
          });
        }
      }
    });

    return trends;
  }
}
